<template>
    <div class="picking-modal" role="presentation" @mousedown.self="$emit('close')">
        <section class="picking-dialog picking-dialog--completed" role="dialog" aria-modal="true" aria-labelledby="completed-title">
            <header class="picking-dialog__header">
                <div>
                    <p class="picking-completed__status"><i class="ak-icon">check</i> Opgeslagen</p>
                    <div class="picking-completed__title">
                        <h2 id="completed-title">Picklijst {{ picking.code }}</h2>
                        <router-link :to="picking.detailPath">Detailpagina</router-link>
                    </div>
                    <p>
                        <i class="ak-icon">receipt_long</i>
                        Bestelling: <router-link :to="picking.detailPath">{{ picking.orderCode }}</router-link>
                    </p>
                    <p v-if="picking.externalOrderId"><i class="ak-icon">confirmation_number</i> Extern ordernummer: {{ picking.externalOrderId }}</p>
                    <p><i class="ak-icon">person_outline</i> {{ picking.customerName }}</p>
                    <p>
                        <i class="ak-icon">near_me</i> {{ picking.address }}
                        <i v-if="picking.hasSuspiciousShippingAddress"
                           class="ak-icon picking-dialog__address-warning"
                           title="Controleer het leveringsadres: straatnaam en huisnummer lijken verwisseld of dubbel ingevuld."
                           aria-label="Controleer het leveringsadres: straatnaam en huisnummer lijken verwisseld of dubbel ingevuld.">warning</i>
                    </p>
                </div>
                <button type="button" class="picking-dialog__close" aria-label="Sluiten" @click="$emit('close')">
                    <i class="ak-icon">close</i>
                </button>
            </header>

            <div class="picking-completed__body">
                <dl class="picking-completed__picker">
                    <dt>Naam orderpicker</dt>
                    <dd>{{ picking.pickerName }}</dd>
                </dl>
                <section v-if="picking.remarks" class="picking-completed__section picking-completed__section--first">
                    <h3>Interne opmerkingen picking</h3>
                    <p class="picking-completed__remarks">{{ picking.remarks }}</p>
                </section>
                <section
                    v-if="selectedItems.length"
                    class="picking-completed__section"
                    :class="{'picking-completed__section--first': !picking.remarks}"
                >
                    <h3>Bestelling</h3>
                    <div class="picking-completed__items">
                    <div v-for="item in selectedItems" :key="item.id">
                        <span>{{ item.title }} <small>#{{ item.code }}</small></span>
                        <strong>{{ item.picked }}/{{ item.requested }}</strong>
                    </div>
                    </div>
                </section>
                <section v-if="selectedMaterials.length" class="picking-completed__section">
                    <h3>Verpakking</h3>
                    <div class="picking-completed__summary">
                        <div v-for="material in selectedMaterials" :key="material.id">
                            <span>{{ material.title }}</span>
                            <strong>{{ material.amount }}</strong>
                        </div>
                    </div>
                </section>
                <section v-if="hasDelivery" class="picking-completed__section">
                    <h3>Verzending</h3>
                    <div class="picking-completed__summary">
                        <div v-if="picking.shipping.numberOfBoxes">
                            <span>Bpost-labels</span>
                            <strong>{{ picking.shipping.numberOfBoxes }}</strong>
                        </div>
                        <div v-if="picking.shipping.numberOfBoxes">
                            <span>Bpost-pakkettype</span>
                            <strong>{{ picking.shipping.boxType }}</strong>
                        </div>
                        <div v-if="picking.shipping.numberOfBoxes">
                            <span>Totaal gewicht verzending</span>
                            <strong>{{ totalWeight }} kg</strong>
                        </div>
                        <div v-if="picking.shipping.boxType === picking.shipping.otherDelivery">
                            <span>Levering</span>
                            <strong>Ander</strong>
                        </div>
                        <div v-if="picking.shipping.numberOfEnvelopes">
                            <span>Enveloppen te frankeren</span>
                            <strong>{{ picking.shipping.numberOfEnvelopes }}</strong>
                        </div>
                    </div>
                </section>
                <p v-if="error" class="picking-dialog__error" role="alert">{{ error }}</p>
            </div>

            <footer class="picking-completed__footer">
                <div class="picking-completed__documents">
                    <button type="button" :disabled="printing === 'picking-list'" @click="$emit('print', 'picking-list')"><i class="ak-icon">print</i> Picklijst</button>
                    <button type="button" :disabled="printing === 'shipping-list'" @click="$emit('print', 'shipping-list')"><i class="ak-icon">print</i> {{ hasShipping ? 'Leverbon' : 'Leverbon & afronden' }}</button>
                    <button v-if="hasShipping" type="button" :disabled="printing === 'shipping-label'" @click="$emit('print', 'shipping-label')"><i class="ak-icon">print</i> Verzendlabel</button>
                </div>
                <button type="button" class="picking-button picking-button--primary picking-completed__new" @click="$emit('new')">
                    Nieuwe picking
                </button>
            </footer>
        </section>
    </div>
</template>

<script>
export default {
    name: 'PickingV2Completed',
    props: {
        picking: {type: Object, required: true},
        printing: {type: String, default: ''},
        error: {type: String, default: ''},
    },
    computed: {
        selectedItems() {
            return this.picking.items.filter(item => item.picked > 0);
        },
        totalWeight() {
            const grams = this.picking.shipping.shipmentWeightInGrams === null
                ? this.picking.items.reduce((total, item) => total + item.picked * item.weightInGrams, 0)
                : this.picking.shipping.shipmentWeightInGrams;
            return (grams / 1000).toFixed(3).replace('.', ',').padStart(6, '0');
        },
        selectedMaterials() {
            return this.picking.materials.filter(material => material.amount > 0);
        },
        hasShipping() {
            return this.picking.shipping.numberOfBoxes > 0 || this.picking.shipping.numberOfEnvelopes > 0;
        },
        hasDelivery() {
            return this.hasShipping || this.picking.shipping.boxType === this.picking.shipping.otherDelivery;
        },
    },
};
</script>

<style lang="scss">
.picking-dialog--completed {width: min(1000px, 100%);}
.picking-completed__status {margin: 0 0 38px !important; color: #38b600; font-size: 15px !important; font-weight: 600;}
.picking-completed__status .ak-icon {color: #38b600 !important;}
.picking-completed__title {display: flex; align-items: baseline; gap: 12px;}
.picking-completed__title a {color: #298afb; text-decoration: none;}
.picking-completed__body {padding: 38px 30px 44px; overflow-y: auto;}
.picking-completed__picker {display: flex; align-items: center; justify-content: space-between; margin: 0 0 42px; padding: 22px 24px; background: var(--picking-dialog-panel); border-radius: 8px;}
.picking-completed__picker dt, .picking-completed__picker dd {margin: 0;}
.picking-completed__picker dd {font-weight: 600;}
.picking-completed__remarks {margin: 0; padding: 18px 24px; white-space: pre-wrap; background: var(--picking-dialog-panel); border-radius: 8px;}
.picking-completed__body h3 {margin: 0 0 16px; color: var(--picking-dialog-muted); font-size: 16px; font-weight: 400;}
.picking-completed__items {padding: 4px 24px; background: var(--picking-dialog-panel); border-radius: 8px;}
.picking-completed__items > div {display: flex; align-items: center; justify-content: space-between; min-height: 64px; border-bottom: 1px solid var(--picking-dialog-border);}
.picking-completed__items > div:last-child {border-bottom: 0;}
.picking-completed__items small {margin-left: 8px; color: var(--picking-dialog-muted);}
.picking-completed__section {margin-top: 32px;}
.picking-completed__section--first {margin-top: 0;}
.picking-completed__summary {padding: 4px 24px; background: var(--picking-dialog-panel); border-radius: 8px;}
.picking-completed__summary > div {display: flex; align-items: center; justify-content: space-between; gap: 24px; min-height: 52px; border-bottom: 1px solid var(--picking-dialog-border);}
.picking-completed__summary > div:last-child {border-bottom: 0;}
.picking-completed__summary span {color: var(--picking-dialog-muted);}
.picking-completed__summary strong {text-align: right;}
.picking-completed__footer {padding: 24px; background: var(--picking-dialog-panel); border-top: 1px solid var(--picking-dialog-border);}
.picking-completed__documents {display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 8px;}
.picking-completed__documents button {display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 58px; padding: 0 12px; color: #298afb; background: transparent; border: 1px solid #298afb; border-radius: 8px; font: inherit; cursor: pointer;}
.picking-completed__documents button:hover {color: #fff; background: #298afb;}
.picking-completed__documents button:disabled {opacity: .55; cursor: wait;}
.picking-completed__new {width: 100%; margin-top: 12px;}
@media (max-width: 520px) {
    .picking-completed__documents {grid-template-columns: 1fr;}
    .picking-completed__title {align-items: flex-start; flex-direction: column;}
}
</style>
